// src/components/Analytics/ScheduleAnalytics.jsx
import React, { useMemo, useEffect, useState, useCallback } from "react";
import { db } from "../../firebase/config";
import { collection, getDocs, getDoc, doc } from "firebase/firestore";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { DateTime } from "luxon";
import "../../styles/Analytics.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// ─────────────────────────── Date range helpers
const formatMDY = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${m}/${d}/${y}`;
};

function buildRangeFromPreset(preset, tz) {
  const end = DateTime.now()
    .setZone(tz)
    .startOf("day");
  let start = end;

  switch (preset) {
    case "LAST_7":
      start = end.minus({ days: 6 });
      break;
    case "LAST_30":
      start = end.minus({ days: 29 });
      break;
    case "LAST_90":
      start = end.minus({ days: 89 });
      break;
    case "MTD":
      start = end.startOf("month");
      break;
    case "YTD":
      start = end.startOf("year");
      break;
    default:
      start = end.minus({ days: 29 });
  }

  return { startISO: start.toISODate(), endISO: end.toISODate() };
}

function inISODateRange(iso, startISO, endISO, tz) {
  if (!iso) return false;
  const d = DateTime.fromISO(iso, { zone: tz }).startOf("day");
  const s = DateTime.fromISO(startISO, { zone: tz }).startOf("day");
  const e = DateTime.fromISO(endISO, { zone: tz }).startOf("day");
  if (!d.isValid || !s.isValid || !e.isValid) return false;
  const ms = d.toMillis();
  return ms >= s.toMillis() && ms <= e.toMillis();
}

// ✅ Try to find a "schedule date" on the doc (weekStart/startDate/etc.)
function getScheduleDateISO(s, tz) {
  if (!s) return null;

  // Common ISO fields
  const isoCandidates = [
    s.weekStart,
    s.weekStartDate,
    s.startDate,
    s.rangeStart,
    s.date,
  ].filter(Boolean);

  for (const v of isoCandidates) {
    if (typeof v === "string") {
      const dt = DateTime.fromISO(v, { zone: tz });
      if (dt.isValid) return dt.toISODate();
    }
  }

  // Timestamp-like fields
  const tsCandidates = [s.weekStartTs, s.startDateTs, s.createdAt].filter(
    Boolean,
  );

  for (const v of tsCandidates) {
    try {
      if (typeof v?.toDate === "function") {
        const dt = DateTime.fromJSDate(v.toDate(), { zone: tz });
        if (dt.isValid) return dt.toISODate();
      }
      if (typeof v === "number") {
        const dt = DateTime.fromMillis(v, { zone: tz });
        if (dt.isValid) return dt.toISODate();
      }
    } catch {
      // ignore
    }
  }

  return null;
}

// ─────────────────────────── Behavior helpers (Missed / Compliance)
const DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const safeMissedCount = (s) => {
  if (!s) return 0;
  if (typeof s.missedDatesCount === "number") return s.missedDatesCount;
  const md = s.missedDates || {};
  return Object.keys(md).length;
};

const safeScheduledShiftCount = (s) => {
  const shifts = s?.shifts || [];
  return shifts.filter((x) => x?.day && x?.startTime && x?.stopTime).length;
};

const isCallOutShift = (shift) => {
  if (!shift) return false;

  // ✅ matches your Firestore schema: isCallOut: true
  if (shift.isCallOut === true) return true;

  // optional fallbacks (in case other code writes it differently later)
  if (shift.isCallout === true) return true;
  if (shift.callOut === true) return true;
  if (shift.callout === true) return true;

  const t = String(shift.type || "").toUpperCase();
  const s = String(shift.status || "").toUpperCase();

  return (
    t === "CALL_OUT" || t === "CALLOUT" || s === "CALL_OUT" || s === "CALLOUT"
  );
};

const safeCallOutCount = (s) => {
  const shifts = s?.shifts || [];
  // count call-out days (even if start/stop exists)
  return shifts.filter((x) => isCallOutShift(x)).length;
};

/**
 * ✅ UPDATED:
 * - Date range bar (same UX as other analytics)
 * - Filters schedules by schedule "weekStart/startDate/etc" within range
 * - Drilldown still works (employeeId)
 * - ✅ Adds behavior analytics: missed shifts + compliance
 */
export default function ScheduleAnalytics({ companyId, employeeId = null }) {
  const [schedules, setSchedules] = useState([]);
  const [companyTimeZone, setCompanyTimeZone] = useState("UTC");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const isDrilldown = !!employeeId;

  // ✅ Date range state
  const [rangePreset, setRangePreset] = useState("LAST_30");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");

  // Guard (no early return — just set flags)
  useEffect(() => {
    if (!companyId) {
      setError("Company not loaded.");
      setLoading(false);
    } else {
      setError("");
    }
  }, [companyId]);

  // Company timezone (so range defaults + parsing stays consistent)
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "companies", companyId));
        setCompanyTimeZone(snap.data()?.timeZone || "UTC");
      } catch {
        setCompanyTimeZone("UTC");
      }
    })();
  }, [companyId]);

  // Keep rangeStart/rangeEnd in sync with preset (unless CUSTOM)
  useEffect(() => {
    if (!companyTimeZone) return;

    if (rangePreset === "CUSTOM") {
      const todayISO = DateTime.now()
        .setZone(companyTimeZone)
        .toISODate();

      // only fill blanks, don’t overwrite user input
      if (!rangeStart) setRangeStart(todayISO);
      if (!rangeEnd) setRangeEnd(todayISO);
      return;
    }

    const { startISO, endISO } = buildRangeFromPreset(
      rangePreset,
      companyTimeZone,
    );

    // only update if different (prevents re-render loops)
    if (rangeStart !== startISO) setRangeStart(startISO);
    if (rangeEnd !== endISO) setRangeEnd(endISO);
  }, [rangePreset, companyTimeZone, rangeStart, rangeEnd]);

  // ✅ Guard: keep start <= end
  useEffect(() => {
    if (!rangeStart || !rangeEnd) return;
    const s = DateTime.fromISO(rangeStart, { zone: companyTimeZone });
    const e = DateTime.fromISO(rangeEnd, { zone: companyTimeZone });
    if (s.isValid && e.isValid && s > e) setRangeEnd(rangeStart);
  }, [rangeStart, rangeEnd, companyTimeZone]);

  // Fetch schedules
  useEffect(() => {
    const fetchSchedules = async () => {
      if (!companyId) return;

      setLoading(true);
      setError("");

      try {
        const scheduleSnap = await getDocs(
          collection(db, `companies/${companyId}/schedules`),
        );

        const raw = scheduleSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        setSchedules(raw);
      } catch (e) {
        console.error(e);
        setError("Failed to load schedules.");
      } finally {
        setLoading(false);
      }
    };

    fetchSchedules();
  }, [companyId]);

  // helper: compute hours for a schedule doc
  const hoursForSchedule = useCallback((sched) => {
    if (!sched?.shifts?.length) return 0;

    return sched.shifts.reduce((sum, shift) => {
      if (!shift?.startTime || !shift?.stopTime) return sum;

      const [startHour, startMin] = shift.startTime.split(":").map(Number);
      const [endHour, endMin] = shift.stopTime.split(":").map(Number);

      // basic diff (assumes same-day shift)
      const diff = endHour + endMin / 60 - (startHour + startMin / 60);
      return sum + (Number.isFinite(diff) ? diff : 0);
    }, 0);
  }, []);

  // ✅ employee scoped schedules
  const employeeScoped = useMemo(() => {
    if (!employeeId) return schedules;

    return schedules.filter((s) => {
      const schedEmpId = s.employeeId || s.userId || s.uid || null;
      return (schedEmpId || "") === employeeId;
    });
  }, [schedules, employeeId]);

  // ✅ date scoped schedules
  const scopedSchedules = useMemo(() => {
    if (!rangeStart || !rangeEnd) return employeeScoped;

    return employeeScoped.filter((s) => {
      const iso = getScheduleDateISO(s, companyTimeZone);
      if (!iso) return false;
      return inISODateRange(iso, rangeStart, rangeEnd, companyTimeZone);
    });
  }, [employeeScoped, rangeStart, rangeEnd, companyTimeZone]);

  // ───────────────────────────────── Metrics (use scopedSchedules)
  const totalHours = useMemo(() => {
    return scopedSchedules.reduce(
      (sum, sched) => sum + hoursForSchedule(sched),
      0,
    );
  }, [scopedSchedules, hoursForSchedule]);

  // company-wide: unique employees scheduled
  const uniqueEmployees = useMemo(() => {
    if (isDrilldown) return null;
    return new Set(scopedSchedules.map((s) => s.employeeId)).size;
  }, [isDrilldown, scopedSchedules]);

  // Days with most shifts (scheduled)
  const dayCounts = useMemo(() => {
    const counts = {};
    scopedSchedules.forEach((sched) => {
      sched.shifts?.forEach((shift) => {
        const day = shift.day;
        if (!day) return;
        counts[day] = (counts[day] || 0) + 1;
      });
    });
    return counts;
  }, [scopedSchedules]);

  const totalShiftDays = useMemo(() => {
    return Object.values(dayCounts).reduce((a, b) => a + b, 0);
  }, [dayCounts]);

  const busiestDay = useMemo(() => {
    return Object.keys(dayCounts).length
      ? Object.entries(dayCounts).sort((a, b) => b[1] - a[1])[0][0]
      : "—";
  }, [dayCounts]);

  // Average hours label/value
  const { averageHoursLabel, averageHoursValue } = useMemo(() => {
    if (isDrilldown) {
      const avg =
        scopedSchedules.length > 0 ? totalHours / scopedSchedules.length : 0;
      return {
        averageHoursLabel: "Avg Hours / Schedule",
        averageHoursValue: avg.toFixed(2),
      };
    }

    const employeeHoursMap = scopedSchedules.reduce((acc, sched) => {
      const key = sched.employeeName || sched.employeeId || "Unknown";
      acc[key] = (acc[key] || 0) + hoursForSchedule(sched);
      return acc;
    }, {});

    const vals = Object.values(employeeHoursMap);
    const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;

    return {
      averageHoursLabel: "Avg Hours / Employee",
      averageHoursValue: avg.toFixed(2),
    };
  }, [isDrilldown, scopedSchedules, totalHours, hoursForSchedule]);

  const noShiftCount = useMemo(() => {
    return scopedSchedules.filter((s) => !s.shifts?.length).length;
  }, [scopedSchedules]);

  // ───────────────────────────────── Behavior Analytics (Compliance / Missed)
  const scheduledShiftCount = useMemo(() => {
    return scopedSchedules.reduce(
      (sum, s) => sum + safeScheduledShiftCount(s),
      0,
    );
  }, [scopedSchedules]);

  const missedShiftCount = useMemo(() => {
    return scopedSchedules.reduce((sum, s) => sum + safeMissedCount(s), 0);
  }, [scopedSchedules]);

  const callOutShiftCount = useMemo(() => {
    return scopedSchedules.reduce((sum, s) => sum + safeCallOutCount(s), 0);
  }, [scopedSchedules]);

  // ✅ total scheduled shift-days in range (includes call-outs because they still have start/stop)
  const expectedShiftCount = useMemo(() => {
    return scheduledShiftCount;
  }, [scheduledShiftCount]);

  // ✅ call-outs reduce compliance (treated like "not met")
  const metShiftCount = useMemo(() => {
    return Math.max(
      0,
      expectedShiftCount - missedShiftCount - callOutShiftCount,
    );
  }, [expectedShiftCount, missedShiftCount, callOutShiftCount]);

  const compliancePct = useMemo(() => {
    if (!expectedShiftCount) return 0;
    return (metShiftCount / expectedShiftCount) * 100;
  }, [expectedShiftCount, metShiftCount]);

  const missRatePct = useMemo(() => {
    if (!scheduledShiftCount) return 0;
    return (missedShiftCount / scheduledShiftCount) * 100;
  }, [scheduledShiftCount, missedShiftCount]);

  // Missed shifts by Day of Week (from missedDates keys)
  const missedByDay = useMemo(() => {
    const counts = {};
    DAYS.forEach((d) => (counts[d] = 0));

    scopedSchedules.forEach((s) => {
      const md = s.missedDates || {};
      Object.keys(md).forEach((iso) => {
        const dt = DateTime.fromISO(iso, { zone: companyTimeZone });
        if (!dt.isValid) return;

        // Luxon weekday: 1=Mon..7=Sun
        // Convert to our DAYS where index 0 is Sunday
        const dayName = DAYS[dt.weekday % 7];
        counts[dayName] = (counts[dayName] || 0) + 1;
      });
    });

    return counts;
  }, [scopedSchedules, companyTimeZone]);

  // Company-wide: Top missed employees
  const missedByEmployee = useMemo(() => {
    if (isDrilldown) return {};
    const map = {};
    scopedSchedules.forEach((s) => {
      const key = s.employeeName || s.employeeId || "Unknown";
      map[key] = (map[key] || 0) + safeMissedCount(s);
    });
    return map;
  }, [scopedSchedules, isDrilldown]);

  const top5MissedEmployees = useMemo(() => {
    if (isDrilldown) return [];
    return Object.entries(missedByEmployee)
      .map(([name, missed]) => ({ name, missed }))
      .sort((a, b) => b.missed - a.missed)
      .slice(0, 5);
  }, [missedByEmployee, isDrilldown]);

  const topMissedEmployeeLabel = useMemo(() => {
    if (isDrilldown) return null;
    if (!top5MissedEmployees.length) return "—";
    const t = top5MissedEmployees[0];
    return `${t.name} · ${t.missed} missed`;
  }, [isDrilldown, top5MissedEmployees]);

  // ───────────────────────────────── Charts (Shifts by Day)
  const barData = useMemo(() => {
    return {
      labels: DAYS,
      datasets: [
        {
          label: "Shifts Scheduled",
          data: DAYS.map((day) => dayCounts[day] || 0),
          backgroundColor: (ctx) => {
            const { chartArea, ctx: g } = ctx.chart;
            if (!chartArea) return "#00C49F";
            const grad = g.createLinearGradient(
              0,
              chartArea.top,
              0,
              chartArea.bottom,
            );
            grad.addColorStop(0, "rgba(16,185,129,0.9)");
            grad.addColorStop(1, "rgba(250,204,21,0.55)");
            return grad;
          },
          borderRadius: 6,
          borderSkipped: false,
        },
      ],
    };
  }, [dayCounts]);

  const barOptions = useMemo(() => {
    return {
      responsive: true,
      plugins: { legend: { display: false } },
      scales: { y: { beginAtZero: true } },
    };
  }, []);

  // Company-wide ONLY: Top 5 chart + KPI (Scheduled Hours)
  const employeeHoursMap = useMemo(() => {
    if (isDrilldown) return {};
    return scopedSchedules.reduce((acc, sched) => {
      const key = sched.employeeName || sched.employeeId || "Unknown";
      acc[key] = (acc[key] || 0) + hoursForSchedule(sched);
      return acc;
    }, {});
  }, [isDrilldown, scopedSchedules, hoursForSchedule]);

  const top5Employees = useMemo(() => {
    if (isDrilldown) return [];
    return Object.entries(employeeHoursMap)
      .map(([name, hours]) => ({ name, hours }))
      .sort((a, b) => b.hours - a.hours)
      .slice(0, 5);
  }, [isDrilldown, employeeHoursMap]);

  const topEmployee = useMemo(() => {
    if (isDrilldown) return null;
    return top5Employees.length
      ? `${top5Employees[0].name} · ${top5Employees[0].hours.toFixed(1)}h`
      : "—";
  }, [isDrilldown, top5Employees]);

  const top5BarData = useMemo(() => {
    return {
      labels: top5Employees.map((e) => e.name),
      datasets: [
        {
          label: "Scheduled Hours",
          data: top5Employees.map((e) => Number(e.hours.toFixed(2))),
          backgroundColor: (ctx) => {
            const { chartArea, ctx: g } = ctx.chart;
            if (!chartArea) return "#22c55e";
            const grad = g.createLinearGradient(
              chartArea.left,
              0,
              chartArea.right,
              0,
            );
            grad.addColorStop(0, "rgba(34,197,94,0.9)");
            grad.addColorStop(1, "rgba(250,204,21,0.55)");
            return grad;
          },
          borderRadius: 8,
          borderSkipped: false,
        },
      ],
    };
  }, [top5Employees]);

  const top5BarOptions = useMemo(() => {
    return {
      responsive: true,
      indexAxis: "y",
      plugins: {
        legend: { display: false },
        tooltip: { callbacks: { label: (ctx) => `${ctx.raw} hrs` } },
      },
      scales: {
        x: { beginAtZero: true, ticks: { callback: (v) => `${v}h` } },
        y: { grid: { display: false } },
      },
    };
  }, []);

  // Behavior chart: Missed shifts by day
  const missedByDayBarData = useMemo(() => {
    return {
      labels: DAYS,
      datasets: [
        {
          label: "Missed Shifts",
          data: DAYS.map((d) => missedByDay[d] || 0),
          backgroundColor: (ctx) => {
            const { chartArea, ctx: g } = ctx.chart;
            if (!chartArea) return "#fb7185";
            const grad = g.createLinearGradient(
              0,
              chartArea.top,
              0,
              chartArea.bottom,
            );
            grad.addColorStop(0, "rgba(244,63,94,0.9)");
            grad.addColorStop(1, "rgba(250,204,21,0.35)");
            return grad;
          },
          borderRadius: 6,
          borderSkipped: false,
        },
      ],
    };
  }, [missedByDay]);

  const missedByDayBarOptions = useMemo(() => {
    return {
      responsive: true,
      plugins: { legend: { display: false } },
      scales: { y: { beginAtZero: true } },
    };
  }, []);

  // Company-wide ONLY: Top 5 missed employees chart
  const top5MissedBarData = useMemo(() => {
    return {
      labels: top5MissedEmployees.map((e) => e.name),
      datasets: [
        {
          label: "Missed Shifts",
          data: top5MissedEmployees.map((e) => e.missed),
          backgroundColor: (ctx) => {
            const { chartArea, ctx: g } = ctx.chart;
            if (!chartArea) return "#f97316";
            const grad = g.createLinearGradient(
              chartArea.left,
              0,
              chartArea.right,
              0,
            );
            grad.addColorStop(0, "rgba(249,115,22,0.9)");
            grad.addColorStop(1, "rgba(250,204,21,0.45)");
            return grad;
          },
          borderRadius: 8,
          borderSkipped: false,
        },
      ],
    };
  }, [top5MissedEmployees]);

  const top5MissedBarOptions = useMemo(() => {
    return {
      responsive: true,
      indexAxis: "y",
      plugins: {
        legend: { display: false },
        tooltip: { callbacks: { label: (ctx) => `${ctx.raw} missed` } },
      },
      scales: {
        x: { beginAtZero: true, ticks: { callback: (v) => `${v}` } },
        y: { grid: { display: false } },
      },
    };
  }, []);

  // ───────────────────────────────── UI (NO EARLY RETURNS ABOVE HOOKS)
  if (!companyId) return <p>Loading...</p>;
  if (loading) return <p>Loading...</p>;

  return (
    <div className="management-analytics-wrapper">
      {error && <p className="error-text">{error}</p>}

      {/* ✅ Date range controls */}
      <div className="analytics-rangebar">
        <div className="analytics-filter">
          <label className="analytics-filter-label">Date Range</label>
          <select
            className="analytics-dropdown"
            value={rangePreset}
            onChange={(e) => setRangePreset(e.target.value)}
          >
            <option value="LAST_7">Last 7 days</option>
            <option value="LAST_30">Last 30 days</option>
            <option value="LAST_90">Last 90 days</option>
            <option value="MTD">Month to date</option>
            <option value="YTD">Year to date</option>
            <option value="CUSTOM">Custom</option>
          </select>
        </div>

        <div className="analytics-filter">
          <label className="analytics-filter-label">Start</label>
          <input
            className="analytics-date-input"
            type="date"
            value={rangeStart}
            onChange={(e) => {
              setRangePreset("CUSTOM");
              setRangeStart(e.target.value);
            }}
          />
        </div>

        <div className="analytics-filter">
          <label className="analytics-filter-label">End</label>
          <input
            className="analytics-date-input"
            type="date"
            value={rangeEnd}
            onChange={(e) => {
              setRangePreset("CUSTOM");
              setRangeEnd(e.target.value);
            }}
          />
        </div>

        <div className="analytics-range-pill">
          {rangeStart && rangeEnd
            ? `${formatMDY(rangeStart)} → ${formatMDY(rangeEnd)}`
            : "—"}
        </div>
      </div>

      {/* Hint */}
      {employeeId ? (
        <div className="analytics-hint">
          Showing Scheduling analytics for <b>1 employee</b>.
        </div>
      ) : (
        <div className="analytics-hint">
          Showing Scheduling analytics for <b>all employees</b>.
        </div>
      )}

      {/* KPI grid */}
      <div className="kpi-grid">
        <div className="kpi-card accent-teal">
          <div className="kpi-top">
            <span className="kpi-dot" />
            <span className="kpi-label">Total Scheduled Hours</span>
          </div>
          <div className="kpi-value">{totalHours.toFixed(1)}</div>
          <div className="kpi-sub">Across all shifts</div>
        </div>

        {/* Company-wide only */}
        {!isDrilldown && (
          <div className="kpi-card accent-indigo">
            <div className="kpi-top">
              <span className="kpi-dot" />
              <span className="kpi-label">Employees Scheduled</span>
            </div>
            <div className="kpi-value">{uniqueEmployees}</div>
            <div className="kpi-sub">Unique people on the roster</div>
          </div>
        )}

        <div className="kpi-card accent-blue">
          <div className="kpi-top">
            <span className="kpi-dot" />
            <span className="kpi-label">{averageHoursLabel}</span>
          </div>
          <div className="kpi-value">{averageHoursValue}</div>
          <div className="kpi-sub">
            {isDrilldown
              ? "Average per schedule record"
              : "Mean scheduled this period"}
          </div>
        </div>

        <div className="kpi-card accent-red">
          <div className="kpi-top">
            <span className="kpi-dot" />
            <span className="kpi-label">
              {isDrilldown ? "No-Shift Schedules" : "No-Shift Employees"}
            </span>
          </div>
          <div className="kpi-value">{noShiftCount}</div>
          <div className="kpi-sub">
            {isDrilldown ? "Schedules with zero shifts" : "Have zero shifts"}
          </div>
        </div>

        <div className="kpi-card accent-amber">
          <div className="kpi-top">
            <span className="kpi-dot" />
            <span className="kpi-label">Total Shift Days</span>
          </div>
          <div className="kpi-value">{totalShiftDays}</div>
          <div className="kpi-sub">Sum of all day slots</div>
        </div>

        <div className="kpi-card accent-green">
          <div className="kpi-top">
            <span className="kpi-dot" />
            <span className="kpi-label">Busiest Day</span>
          </div>
          <div className="kpi-value">{busiestDay}</div>
          <div className="kpi-sub">Most shifts scheduled</div>
        </div>

        {/* ✅ Company-wide ONLY: Top Employee KPI */}
        {!isDrilldown && (
          <div className="kpi-card accent-purple">
            <div className="kpi-top">
              <span className="kpi-dot" />
              <span className="kpi-label">Top Employee</span>
            </div>
            <div className="kpi-value" style={{ fontSize: "1.35rem" }}>
              {topEmployee || "—"}
            </div>
            <div className="kpi-sub">Highest scheduled hours</div>
          </div>
        )}

        {/* ✅ Behavior KPIs */}
        <div className="kpi-card accent-rose">
          <div className="kpi-top">
            <span className="kpi-dot" />
            <span className="kpi-label">Missed Shifts</span>
          </div>
          <div className="kpi-value">{missedShiftCount}</div>
          <div className="kpi-sub">Missed scheduled shift-days</div>
        </div>
        <div className="kpi-card accent-orange">
          <div className="kpi-top">
            <span className="kpi-dot" />
            <span className="kpi-label">Call-Outs</span>
          </div>
          <div className="kpi-value">{callOutShiftCount}</div>
          <div className="kpi-sub">Marked as call-out shift-days</div>
        </div>

        <div className="kpi-card accent-emerald">
          <div className="kpi-top">
            <span className="kpi-dot" />
            <span className="kpi-label">Schedule Compliance</span>
          </div>
          <div className="kpi-value">
            {scheduledShiftCount ? `${compliancePct.toFixed(1)}%` : "—"}
          </div>
          <div className="kpi-sub">
            {expectedShiftCount
              ? `${metShiftCount}/${expectedShiftCount} shifts met (missed + call-outs reduce compliance)`
              : "No scheduled shifts in range"}
          </div>
        </div>

        <div className="kpi-card accent-orange">
          <div className="kpi-top">
            <span className="kpi-dot" />
            <span className="kpi-label">Miss Rate</span>
          </div>
          <div className="kpi-value">
            {scheduledShiftCount ? `${missRatePct.toFixed(1)}%` : "—"}
          </div>
          <div className="kpi-sub">Missed / Scheduled</div>
        </div>

        {!isDrilldown && (
          <div className="kpi-card accent-slate">
            <div className="kpi-top">
              <span className="kpi-dot" />
              <span className="kpi-label">Top Missed Employee</span>
            </div>
            <div className="kpi-value" style={{ fontSize: "1.25rem" }}>
              {topMissedEmployeeLabel}
            </div>
            <div className="kpi-sub">Most missed shifts in range</div>
          </div>
        )}
      </div>

      {/* Charts */}
      <div className="charts-grid">
        <div className="chart-container">
          <h3>
            {isDrilldown
              ? "Employee Shifts by Day of Week"
              : "Shifts by Day of Week"}
          </h3>
          <Bar data={barData} options={barOptions} />
        </div>

        {/* ✅ Company-wide ONLY: Top 5 chart (Scheduled Hours) */}
        {!isDrilldown && (
          <div className="chart-container" style={{ minHeight: 360 }}>
            <h3>Top 5 Employees with Most Scheduled Hours</h3>
            <Bar data={top5BarData} options={top5BarOptions} />
          </div>
        )}

        {/* ✅ Behavior chart: Missed shifts by day of week */}
        <div className="chart-container">
          <h3>Missed Shifts by Day of Week</h3>
          <Bar data={missedByDayBarData} options={missedByDayBarOptions} />
        </div>

        {/* ✅ Company-wide ONLY: Top 5 missed employees */}
        {!isDrilldown && (
          <div className="chart-container" style={{ minHeight: 360 }}>
            <h3>Top 5 Employees with Most Missed Shifts</h3>
            <Bar data={top5MissedBarData} options={top5MissedBarOptions} />
          </div>
        )}
      </div>
    </div>
  );
}
